import React, { useState, useRef } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import classNames from 'classnames';
import Dropdown from 'react-bootstrap/Dropdown';
import { DropdownToggle } from '../dropdown';
import { queries, usePartyIds } from '../../api';
import { getTeamById } from '../../api/queries';
import { useAuth } from '../../auth';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { A, paths } from '../../routing';
import { useTeamInfo } from '../../team-id-context';
import { get as getLatestTeamId, set } from '../../utils/latest-team-id';
import CreateNewTeamModal from '../shared/modals/createNewTeamModal/CreateNewTeamModal';
import { ReactComponent as WorkspacesIcon } from '../../shared/icons/workspaces_icon.svg';
import { SubscriptionRequiredModal } from '../../pages';
import styles from './styles.module.scss';

const CurrentWorkspaceIndicator = () => {
  const [{ authLoading: isAuthLoading, user }] = useAuth();
  const { canCreateTeam } = useUserRestrictions();
  const teamId = useTeamInfo().teamId ?? getLatestTeamId(user.id);
  const [showAddNewTeamModal, setShowAddNewTeamModal] = useState(false);
  const [showSubscriptionRequiredModal, setSubscriptionRequiredModal] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(teamId);
  const [refetchUserRestrictions] = useLazyQuery(queries.getRestrictionsByCurrentUser, {
    fetchPolicy: 'network-only',
  });

  const { loading: isPartyIdsLoading, partyIds } = usePartyIds();
  const { loading: isTeamLoading, data: teamData } = useQuery(getTeamById, {
    skip: !teamId,
    variables: { teamId },
  });

  const onAddTeamClick = () => (canCreateTeam ? setShowAddNewTeamModal(true) : setSubscriptionRequiredModal(true));

  const loading = isAuthLoading || isPartyIdsLoading || isTeamLoading;

  const [menuOpen, setMenuOpen] = useState(false);

  const forceOpenRef = useRef(false);

  // `user.allTeams` can be missing when the session is unauthenticated/partial
  // (e.g. an expired token → the user query 401s and returns no teams). Guard so
  // the layout degrades gracefully instead of crashing the whole app.
  const sortedTeams = [...(user?.allTeams ?? [])].sort((a: { id: string; name: string }, b: { id: string; name: string }) => {
    const aIsActive = a.id === teamId || a.id === selectedWorkspaceId;
    const bIsActive = b.id === teamId || b.id === selectedWorkspaceId;
    if (aIsActive && !bIsActive) return -1;
    if (bIsActive && !aIsActive) return 1;
    return a.name.localeCompare(b.name);
  });

  const userLocationToRedirect = () => {
    // Always redirect to deals landing page when switching workspaces
    // This ensures users never land on pages they don't have access to
    return paths.deals.landing;
  };

  const handleDropdownToggle = (show: boolean) => {
    if (forceOpenRef.current) {
      setMenuOpen(true);
      forceOpenRef.current = false;
      return;
    }

    setMenuOpen(show);
  };

  return (
    <>
      <Dropdown alignRight show={menuOpen} onToggle={handleDropdownToggle}>
        <Dropdown.Toggle
          id="team-selector"
          data-testid="team-selector"
          as={DropdownToggle}
          className="no-outline d-inline-flex text-left"
          contentClassName="d-flex align-items-center"
          // @ts-ignore
          style={{
            top: 20,
            right: 20,
          }}
        >
          <span className={styles.workspacesItem}>
            <WorkspacesIcon height="30" fill="#274156" />
          </span>
          {!loading && partyIds != null && (
            <div style={{ lineHeight: 1, maxWidth: '200px', paddingLeft: 0 }}>
              <div className="small text-muted">Workspace</div>
              <div
                style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                className="medium text-muted font-weight-bold pt-1"
              >
                {teamData?.teamById.name}
              </div>
            </div>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.workspacesMenu}>
          {teamData && (
            <>
              <div className={styles.workspacesMenuList}>
                {sortedTeams.map(({ id, name }: { id: string; name: string }) => (
                  <Dropdown.Item
                    key={id}
                    onClick={() => {
                      set(user.id, id);
                      setSelectedWorkspaceId(id);
                      refetchUserRestrictions({ variables: { teamId } });
                    }}
                    as={A}
                    href={userLocationToRedirect()}
                    hrefParams={{ teamId: id }}
                    className={
                      styles[
                        `${
                          id === teamId || id === selectedWorkspaceId ? 'workspaceOption__active' : 'workspaceOption'
                        }`
                      ]
                    }
                  >
                    {name}
                  </Dropdown.Item>
                ))}
              </div>
              <Dropdown.Item
                key="addNewTeam.button"
                className={classNames(styles.workspaceOption__newWorkspace)}
                onClick={onAddTeamClick}
              >
                + New Workspace
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <CreateNewTeamModal show={showAddNewTeamModal} handleHide={() => setShowAddNewTeamModal(false)} />
      <SubscriptionRequiredModal
        isOpen={showSubscriptionRequiredModal}
        onClose={() => setSubscriptionRequiredModal(false)}
      />
    </>
  );
};

export default CurrentWorkspaceIndicator;
